/**
 * Stripe Connect status + actions panel for use on PaymentProcessorEdit.
 *
 * Only render this when processor.processor === "stripe". It shows the
 * current Stripe Connect state and exposes the same actions that used to live
 * on the /stripe-connect page (Refresh, Continue Onboarding, Open Stripe
 * Dashboard, Unlink, Relink previous account).
 *
 * Everything wires through the existing /api/stripeConnect/* endpoints, so
 * the ownership/scope guards are enforced server-side just like before.
 */

import { useState } from "react";
import { toast } from "react-hot-toast";
import {
  createLoginLink,
  createOnboardingLink,
  disconnectAccount,
  refreshAccountStatus,
  relinkPreviousAccount,
} from "./api";
import { TerminalLocationModal } from "./TerminalLocationModal";

interface StripeConfigBits {
  connected_account_id?: string | null;
  previous_connected_account_id?: string | null;
  platform_account_location_id?: string | null;
  stripe_charges_enabled?: boolean;
  stripe_payouts_enabled?: boolean;
  stripe_details_submitted?: boolean;
  stripe_onboarded_at?: string | null;
  stripe_mode?: string | null;
}

interface Props {
  processorId: number;
  processorName: string;
  config: StripeConfigBits | null | undefined;
}

function statusLabel(c: StripeConfigBits | null | undefined): string {
  if (!c?.connected_account_id) return "Not Connected";
  const fully =
    !!c.stripe_charges_enabled &&
    !!c.stripe_payouts_enabled &&
    !!c.stripe_details_submitted;
  if (fully) return "Active";
  if (c.stripe_details_submitted) return "Pending Verification";
  return "Onboarding Incomplete";
}

function statusDotColor(c: StripeConfigBits | null | undefined): string {
  if (!c?.connected_account_id) return "bg-gray-300";
  const fully =
    !!c.stripe_charges_enabled &&
    !!c.stripe_payouts_enabled &&
    !!c.stripe_details_submitted;
  if (fully) return "bg-green-500";
  return "bg-yellow-500";
}

export const StripeConnectStatusPanel = ({
  processorId,
  processorName,
  config,
}: Props) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const locationId = config?.platform_account_location_id || null;
  const connected = !!config?.connected_account_id;
  const onboarded =
    !!config?.stripe_charges_enabled &&
    !!config?.stripe_payouts_enabled &&
    !!config?.stripe_details_submitted;
  const previous = config?.previous_connected_account_id || null;

  const reload = () => {
    // The host page reads from useLoaderData and doesn't expose a revalidator
    // here, so a soft reload is the simplest way to reflect changes.
    window.location.reload();
  };

  const onRefresh = async () => {
    setBusy("refresh");
    try {
      await refreshAccountStatus(processorId);
      toast.success("Stripe status refreshed");
      reload();
    } catch (err: any) {
      toast.error(`Refresh failed: ${err.message || "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  const onConnect = async () => {
    setBusy("connect");
    try {
      const returnUrl = `${window.location.origin}/payment-processors/list?onboarding=complete&processorId=${processorId}`;
      const refreshUrl = `${window.location.origin}/payment-processors/list?onboarding=refresh&processorId=${processorId}`;
      const { url } = await createOnboardingLink(
        processorId,
        returnUrl,
        refreshUrl
      );
      window.location.href = url;
    } catch (err: any) {
      toast.error(`Stripe Connect: ${err.message || "failed to start"}`);
      setBusy(null);
    }
  };

  const onOpenDashboard = async () => {
    setBusy("dashboard");
    try {
      const { url } = await createLoginLink(processorId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      toast.error(`Stripe Dashboard: ${err.message || "failed to open"}`);
    } finally {
      setBusy(null);
    }
  };

  const onUnlink = async () => {
    if (
      !window.confirm(
        `Unlink the Stripe account from "${processorName}"?\n\nThis only removes the link in our system — the Stripe account itself stays active and is NOT closed. The account id is stashed so you can one-click "Relink" later.\n\nTo actually shut down the merchant, reject the account from the Stripe Dashboard.`
      )
    ) {
      return;
    }
    setBusy("unlink");
    try {
      await disconnectAccount(processorId);
      toast.success("Stripe account unlinked");
      reload();
    } catch (err: any) {
      toast.error(`Unlink failed: ${err.message || "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  const onRelink = async () => {
    if (!previous) {
      toast.error("No previous Stripe account to relink");
      return;
    }
    if (
      !window.confirm(
        `Relink the previous Stripe account (${previous}) to "${processorName}"?\n\nThis re-attaches the existing Stripe account — no new account is created and no onboarding is required.`
      )
    ) {
      return;
    }
    setBusy("relink");
    try {
      await relinkPreviousAccount(processorId);
      toast.success("Stripe account relinked");
      reload();
    } catch (err: any) {
      toast.error(`Relink failed: ${err.message || "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white mb-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold mb-1">Stripe Connect status</div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-2 h-2 rounded-full ${statusDotColor(config)}`}
            />
            <span className="text-sm">{statusLabel(config)}</span>
            {config?.stripe_mode && (
              <span className="text-[0.65rem] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                {config.stripe_mode}
              </span>
            )}
          </div>
          {config?.connected_account_id && (
            <div className="text-xs text-gray-500 font-mono mt-1">
              {config.connected_account_id}
            </div>
          )}
          {locationId && (
            <div className="text-xs text-gray-500 font-mono mt-1">
              Terminal location: {locationId}
            </div>
          )}
          {connected && !locationId && (
            <div className="text-xs text-yellow-700 mt-1">
              No Terminal location set. Add one to accept card-reader payments.
            </div>
          )}
          {!connected && previous && (
            <div className="text-xs text-gray-500 mt-1">
              Previous account on file:{" "}
              <span className="font-mono">{previous}</span>
            </div>
          )}
          {config?.stripe_onboarded_at && (
            <div className="text-xs text-gray-400 mt-1">
              Onboarded {new Date(config.stripe_onboarded_at).toLocaleString()}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          {!connected && (
            <button
              type="button"
              onClick={onConnect}
              disabled={!!busy}
              className="whitespace-nowrap text-xs rounded bg-primary-600 text-white font-medium px-3 py-1.5 hover:bg-primary-700 disabled:opacity-50"
            >
              {busy === "connect"
                ? "Connecting..."
                : previous
                  ? "Connect new"
                  : "Connect with Stripe"}
            </button>
          )}
          {!connected && previous && (
            <button
              type="button"
              onClick={onRelink}
              disabled={!!busy}
              className="whitespace-nowrap text-xs rounded border border-primary-600 text-primary-700 font-medium px-3 py-1.5 hover:bg-primary-50 disabled:opacity-50"
              title={`Re-attach the previous Stripe account (${previous})`}
            >
              {busy === "relink" ? "Relinking..." : "Relink previous"}
            </button>
          )}
          {connected && !onboarded && (
            <button
              type="button"
              onClick={onConnect}
              disabled={!!busy}
              className="whitespace-nowrap text-xs rounded bg-primary-600 text-white font-medium px-3 py-1.5 hover:bg-primary-700 disabled:opacity-50"
            >
              {busy === "connect" ? "Loading..." : "Continue setup"}
            </button>
          )}
          {connected && (
            <button
              type="button"
              onClick={onOpenDashboard}
              disabled={!!busy}
              className="whitespace-nowrap text-xs rounded border border-gray-300 bg-white text-gray-700 font-medium px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
            >
              {busy === "dashboard" ? "Opening..." : "Open Stripe Dashboard"}
            </button>
          )}
          {connected && (
            <button
              type="button"
              onClick={() => setLocationModalOpen(true)}
              disabled={!!busy}
              className="whitespace-nowrap text-xs rounded border border-gray-300 bg-white text-gray-700 font-medium px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
              title={locationId ? `Edit Terminal location ${locationId}` : "Add a Terminal Location so this account can accept card-reader payments"}
            >
              {locationId ? "Edit Terminal location" : "Add Terminal location"}
            </button>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={!!busy}
            className="whitespace-nowrap text-xs rounded border border-gray-300 bg-white text-gray-700 font-medium px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
            title="Pull the latest status from Stripe"
          >
            {busy === "refresh" ? "Refreshing..." : "Refresh status"}
          </button>
          {connected && (
            <button
              type="button"
              onClick={onUnlink}
              disabled={!!busy}
              className="whitespace-nowrap text-xs rounded text-red-600 hover:bg-red-50 font-medium px-3 py-1.5 disabled:opacity-50"
            >
              {busy === "unlink" ? "Unlinking..." : "Unlink"}
            </button>
          )}
        </div>
      </div>
      {locationModalOpen && (
        <TerminalLocationModal
          processorId={processorId}
          processorName={processorName}
          existingLocationId={locationId}
          onClose={() => setLocationModalOpen(false)}
          onSaved={() => window.location.reload()}
        />
      )}
    </div>
  );
};
